import qz from 'qz-tray';
import cookies from 'js-cookie';
import { KEYUTIL, KJUR, hextorstr, stob64 } from 'jsrsasign';
import digitalCertificate from './digital-certificate.txt?raw';

// Thin wrapper around qz-tray; lets us override paper sizes per print job.
export type PrintDataOptions = {
  type?: string;
  format?: string;
  flavor?: string;
  ignoreTransparency?: boolean;
  altFontRendering?: boolean;
};

export type PrintDataItem = {
  type?: string;
  format?: string;
  flavor?: string;
  data: string | Uint8Array;
  options?: Record<string, unknown>;
};

export type PrintData = string | PrintDataItem | PrintDataItem[];
export type PrintConfig = Record<string, unknown> & {
  size?: Record<string, unknown>;
};

export default class Printer {
  privateKey?: string;
  publicKey?: string;

  constructor() {
    this.privateKey = import.meta.env.REACT_APP_QZ_PRIVATE_KEY;
    this.publicKey = digitalCertificate;
    this.publicKey && this._setCertificatePromise();
    this.privateKey && this._setSignature();
  }

  _setCertificatePromise = () => {
    qz.security.setCertificatePromise((resolve: (value: string) => void) => {
      resolve(this.publicKey?.toString() || '');
    });
  };

  _setSignature = () => {
    qz.security.setSignatureAlgorithm('SHA512'); // Since 2.1
    qz.security.setSignaturePromise((toSign: string) => {
      return (resolve: (value: string) => void, reject: (reason?: unknown) => void) => {
        try {
          const pk = KEYUTIL.getKey(this.privateKey?.toString() || '');
          const sig = new KJUR.crypto.Signature({ alg: 'SHA512withRSA' });
          sig.init(pk);
          sig.updateString(toSign);
          const hex = sig.sign();
          resolve(stob64(hextorstr(hex)));
        } catch (err) {
          console.error(err);
          reject(err);
        }
      };
    });
  };

  verifyQZConnection = async (targetPrinterName?: string) => {
    try {
      // Specific printer if provided; fallback to ticket printer preference
      const printerName =
        targetPrinterName || cookies.get('printer') || localStorage.getItem('printer');
      if (printerName) {
        qz.printers.startListening(printerName).catch((err: any) => {
          console.error(err);
        });
      }
      // verify it already accepted the QZ Connection
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect(); // this returns undefined by default
      }
      return {
        ok: true,
        message: 'Conexion a QZ Tray funcionando'
      };
    } catch (e) {
      return {
        ok: false,
        message:
          'No pudimos conectar impresora, por favor verifique su configuracion y conexion a QZ Tray. Asegurese de tener en su navegador "Aplicaciones en este dispositivo" encendido'
      };
    }
  };

  print = async (
    printerName: string,
    printConfig: PrintConfig = { size: {} },
    printData: PrintData,
    printDataOptions: PrintDataOptions = {}
  ) => {
    try {
      // Allow caller to override size; fallback to ticket paper size
      const {
        type = 'pixel',
        format = 'html',
        flavor = 'plain',
        ignoreTransparency = false,
        altFontRendering = false
      } = printDataOptions;

      const normalizeItem = (item: any) => {
        if (item && typeof item === 'object' && 'data' in item) {
          const existingOptions =
            item.options && typeof item.options === 'object' ? item.options : {};
          return {
            type,
            format,
            flavor,
            options: {
              ignoreTransparency,
              altFontRendering,
              ...existingOptions
            },
            ...item
          };
        }
        return {
          // IF WE CHANGETHIS, VERIFY PRINTING COPIES FROM CONFIG WORKS
          type,
          // IF WE CHANGETHIS, VERIFY PRINTING COPIES FROM CONFIG WORKS
          format,
          flavor,
          data: item,
          options: { ignoreTransparency, altFontRendering }
        };
      };

      const data = Array.isArray(printData)
        ? printData.map(normalizeItem)
        : [normalizeItem(printData)];

      const configInput: any = {
        ...printConfig,
        size: printConfig.size ? { ...printConfig.size } : {}
      };
      const isRawPrintJob = data.every((item) => item?.type === 'raw');

      if (!isRawPrintJob && !configInput.size.width) {
        const paperSize =
          cookies.get('printerPaperSize') || localStorage.getItem('printerPaperSize');
        const paperWidth = paperSize === '80mm' ? 80 : 58;
        configInput.size.width = paperWidth;
      }

      const config = qz.configs.create(printerName, configInput);

      const printResponse = await qz.print(config, data);
      return printResponse;
    } catch (e) {
      throw e;
    }
  };
}
